import React from 'react';

import { useGetUser, useCreateUser } from '@vlabs/api/artemy/client';
import type { UserToBeCreated } from '@vlabs/api/artemy/model';
import { DuplicateIcon } from '@vlabs/icons';
import { withPermissions } from '@vlabs/permissions';
import { useModal } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { toast } from 'react-toastify';

import { UserForm } from '@vlabs/monokit/entities/users';
import type { UserFormValues } from '@vlabs/monokit/entities/users';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useDuplicateUserTranslation } from './i18n';
import type { DuplicateUserProps } from './types';

type DuplicateUserIconButtonProps = DuplicateUserProps & IconButtonProps;

const displayName = 'DuplicateUserIconButton';
const testid = buildTestid(displayName);

function DuplicateUserIconButtonBase({
  userId,
  'data-testid': dataTestId = undefined,
  ...props
}: DuplicateUserIconButtonProps): React.JSX.Element {
  const { t } = useDuplicateUserTranslation();
  const modal = useModal({ size: 'small' });

  const { data: user } = useGetUser(userId);
  const createUser = useCreateUser({
    mutation: {
      onSuccess: () => {
        toast.success(t('toast_success'));
        modal.close();
      },
      onError: () => {
        toast.error(t('toast_error'));
      },
    },
  });

  const defaultValues = {
    full_name: user?.full_name ? `Copy_of_${user.full_name}` : '',
    email: user?.email ? `Copy_of_${user.email}` : '',
    role_id: user?.role_id ?? undefined,
  };
  const duplicateSourceName = user?.full_name ?? user?.email;

  const handleSubmit = async (values: UserFormValues): Promise<void> => {
    await createUser.mutateAsync({
      data: values as UserToBeCreated,
    });
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <UserForm
          context="duplicate"
          defaultValues={defaultValues}
          duplicateSourceName={duplicateSourceName}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <IconButton
        icon={<DuplicateIcon />}
        kind="secondary"
        onClick={modal.open}
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      />
    </React.Fragment>
  );
}

DuplicateUserIconButtonBase.displayName = displayName;
const DuplicateUserIconButton = withPermissions('users', 'create', { fallback: null })(DuplicateUserIconButtonBase);

export { DuplicateUserIconButton };
